// Circular avatar. Read-only by default; pass `editable` + `onPhotoChange` to wire
// up the upload affordance. Falls back to a serif initial when no photo.

import { useRef, useState } from "react";
import { compressPhoto } from "../lib/photo";
import { useT } from "../lib/LocaleContext";

type Props = {
  size?: number;
  name: string;
  photoDataUrl?: string | null;
  editable?: boolean;
  onPhotoChange?: (dataUrl: string) => Promise<void> | void;
};

export function BabyAvatar({ size = 56, name, photoDataUrl, editable, onPhotoChange }: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const t = useT();
  const initial = name?.[0]?.toUpperCase() ?? "·";

  async function handleFile(f: File) {
    setErr(null);
    setBusy(true);
    try {
      const url = await compressPhoto(f);
      if (onPhotoChange) await onPhotoChange(url);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  const inner = photoDataUrl ? (
    <img src={photoDataUrl} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
  ) : editable ? (
    <span style={{ fontSize: Math.max(10, Math.round(size * 0.18)), color: "var(--bh-ink-3)" }}>{t.babyAvatar.addPhoto}</span>
  ) : (
    <span>{initial}</span>
  );

  const shellStyle: React.CSSProperties = {
    width: size,
    height: size,
    flex: `0 0 ${size}px`,
    borderRadius: "50%",
    boxShadow: `0 0 0 1px var(--bh-divider), 0 0 0 4px var(--bh-bg)`,
    background: "var(--bh-surface)",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--bh-ink-3)",
    fontFamily: "var(--bh-font-serif)",
    fontWeight: 500,
    fontSize: Math.max(14, Math.round(size * 0.42)),
    opacity: busy ? 0.5 : 1,
  };

  if (!editable) {
    return (
      <div style={shellStyle} aria-label={t.babyAvatar.avatarLabel(name)}>
        {inner}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        style={{ ...shellStyle, border: "none", padding: 0, cursor: "pointer" }}
        onClick={() => fileRef.current?.click()}
        aria-label={photoDataUrl ? t.babyAvatar.changePhoto : t.babyAvatar.addPhoto}
        title={err ?? undefined}
      >
        {inner}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = ""; // allow re-selecting the same file
        }}
      />
    </>
  );
}
